import * as Sentry from '@sentry/nextjs';
import { useCallback } from 'react';
import { create } from 'zustand';

import { BlockProgressEntryModel, GoalProgressModel, TaskProgressModel } from '@models/progress';
import { useStore } from '@store';

// --- private store (not exported) ---
interface ProgressState {
  taskProgress: Record<string, TaskProgressModel>;
  setTaskProgress: (taskEntityId: string, progress: TaskProgressModel) => void;
  taskBlocksProgress: Record<string, BlockProgressEntryModel[]>;
  setTaskBlocksProgress: (key: string, blocks: BlockProgressEntryModel[]) => void;
  goalProgressByEntityId: Record<string, GoalProgressModel>;
  setGoalProgress: (goalEntityId: string, progress: GoalProgressModel) => void;
}

const useProgressStore = create<ProgressState>((set, get) => ({
  taskProgress: {},
  setTaskProgress: (taskEntityId, progress) =>
    set({
      taskProgress: {
        ...get().taskProgress,
        [taskEntityId]: progress
      }
    }),
  taskBlocksProgress: {},
  setTaskBlocksProgress: (key, blocks) =>
    set({
      taskBlocksProgress: {
        ...get().taskBlocksProgress,
        [key]: blocks
      }
    }),
  goalProgressByEntityId: {},
  setGoalProgress: (goalEntityId, progress) =>
    set({
      goalProgressByEntityId: {
        ...get().goalProgressByEntityId,
        [goalEntityId]: progress
      }
    })
}));

function blocksProgressKey(taskEntityId: string, goalSubscriptionId?: number): string {
  return goalSubscriptionId != null ? `${goalSubscriptionId}:${taskEntityId}` : taskEntityId;
}

// --- public hook ---
export function useProgressService() {
  const imprvApi = useStore((s) => s.imprvApi);
  const {
    taskProgress,
    setTaskProgress,
    taskBlocksProgress,
    setTaskBlocksProgress,
    goalProgressByEntityId,
    setGoalProgress
  } = useProgressStore();

  const getTaskProgress = useCallback(
    async (taskEntityId: string) => {
      try {
        if (!imprvApi) return;
        const progress = await imprvApi.getTaskProgress(taskEntityId);
        setTaskProgress(taskEntityId, progress);
      } catch (error) {
        Sentry.captureException(error, { extra: { taskEntityId } });
      }
    },
    [imprvApi, setTaskProgress]
  );

  // Stable identity per cache snapshot — this getter sits in useMemo deps at its call sites.
  const getCachedTaskProgress = useCallback(
    (taskEntityId: string): TaskProgressModel | undefined => taskProgress[taskEntityId],
    [taskProgress]
  );

  const getTaskBlocksProgress = useCallback(
    async (taskEntityId: string, goalSubscriptionId?: number) => {
      try {
        if (!imprvApi) return;
        const response = await imprvApi.getTaskBlocksProgress(taskEntityId, goalSubscriptionId);
        setTaskBlocksProgress(blocksProgressKey(taskEntityId, goalSubscriptionId), response.blocks);
      } catch (error) {
        Sentry.captureException(error, { extra: { taskEntityId, goalSubscriptionId } });
      }
    },
    [imprvApi, setTaskBlocksProgress]
  );

  const getCachedTaskBlocksProgress = (
    taskEntityId: string,
    goalSubscriptionId?: number
  ): BlockProgressEntryModel[] | undefined =>
    taskBlocksProgress[blocksProgressKey(taskEntityId, goalSubscriptionId)];

  const getGoalProgress = useCallback(
    async (goalEntityId: string) => {
      try {
        if (!imprvApi) return;
        const progress = await imprvApi.getGoalProgress(goalEntityId);
        setGoalProgress(goalEntityId, progress);
      } catch (error) {
        Sentry.captureException(error, { extra: { goalEntityId } });
      }
    },
    [imprvApi, setGoalProgress]
  );

  const getCachedGoalProgress = (goalEntityId: string): GoalProgressModel | undefined =>
    goalProgressByEntityId[goalEntityId];

  return {
    taskProgress,
    getTaskProgress,
    getCachedTaskProgress,
    taskBlocksProgress,
    getTaskBlocksProgress,
    getCachedTaskBlocksProgress,
    goalProgressByEntityId,
    getGoalProgress,
    getCachedGoalProgress
  };
}
